import React, { useContext } from "react";
import { Form, Select, Col, Switch, Row, Tooltip } from "antd";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl, defineMessages } from "react-intl";
import styled from "styled-components";
import { QuestionCircleFilled } from "@ant-design/icons";

import { FormV5Context } from "../../../../../NodeChannelFormV5";

import {
  SELECT_STREAM_CODEC_AUDIO,
  SELECT_TRANSCODING_CODEC_AUDIO,
  MPEG2AUDIO_BITRATE,
  AAC_PROFILE,
  OUTPUTS_TYPES,
  SELECT_TRANSCODING_RTMP_CODEC_AUDIO,
  MPEG2AUDIO_SAMPLE_FORMAT,
} from "../../../../../../../lib/utils/constants";
import { required, pidValidation } from "../../../../../../../lib/utils/formValidation";
import globalTranslations from "../../../../../../../lib/translations";

import AACCodec from "./AACCodec";
import Mpeg2AudioCodec from "./Mpeg2AudioCodec";
import GlobalAudioCodecFormItem from "../../../../GlobalCodecSection/GlobalCodecAudio/GlobalAudioCodecFormItem";
import InputNumberField from "../../../../../../Fields/InputNumberField";
import AdvancedSettings from "../../../../../../AdvancedSettings";

const { Item } = Form;
const { Option } = Select;

const translations = defineMessages({
  automaticAs0Tooltip: {
    id: "AudioCodec.automaticAs0Tooltip",
    defaultMessage: "Select 0 for automatic",
  },
});

export default function AudioCodec({ disabled, prefix, fieldPrefix }) {
  const { formatMessage } = useIntl();
  const form = useContext(FormV5Context);
  const { setFieldValue, getFieldValue } = form;

  const audioCodecType = Form.useWatch([...prefix, "audioCodec", "type"], form);
  const isSwitchConvert = Form.useWatch([...prefix, "convert"], form) || false;
  const outputType = Form.useWatch([prefix[0], prefix[1], "type"], form);
  const isOutputRtmp = outputType === OUTPUTS_TYPES.outputRtmp.value;

  const isMpeg2audioType = audioCodecType === SELECT_STREAM_CODEC_AUDIO[1].value;
  const isAACType = audioCodecType === SELECT_STREAM_CODEC_AUDIO[2].value;

  const handleChangeCodecType = (codecType) => {
    if (codecType === SELECT_STREAM_CODEC_AUDIO[1].value) {
      setFieldValue(prefix, {
        convert: false,
        pid: 0,
        audioStreamSelector: 0,
        audioCodec: {
          b: MPEG2AUDIO_BITRATE[9].value,
          type: codecType,
        },
      });

      return;
    }

    if (codecType === SELECT_STREAM_CODEC_AUDIO[2].value) {
      setFieldValue(prefix, {
        convert: false,
        pid: 0,
        audioStreamSelector: 0,
        audioCodec: {
          b: MPEG2AUDIO_BITRATE[9].value,
          aacProfile: AAC_PROFILE[0].value,
          type: codecType,
        },
      });

      return;
    }

    setFieldValue([...prefix, "audioCodec"], { type: codecType });
  };

  const handleSwitchConvert = (value) => {
    const audioStream = getFieldValue(prefix);
    if (value) {
      setFieldValue([...prefix, "audioFormat"], { sampleFormat: MPEG2AUDIO_SAMPLE_FORMAT[3].value });
    } else {
      delete audioStream.audioFormat;
      setFieldValue(prefix, audioStream);
    }
  };

  return (
    <>
      <Item
        name={[...fieldPrefix, "audioCodec", "type"]}
        label={<FormattedMessage id="AudioCodec.codec" defaultMessage="Codec" />}
        rules={[required]}
      >
        <Select onChange={handleChangeCodecType} disabled={disabled}>
          {isOutputRtmp &&
            SELECT_TRANSCODING_RTMP_CODEC_AUDIO.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          {!isOutputRtmp &&
            SELECT_TRANSCODING_CODEC_AUDIO.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
        </Select>
      </Item>
      <AdvancedSettings>
        <Row gutter={[24, 24]}>
          <InputNumberField name={[...fieldPrefix, "outputId"]} label="OutputId" disabled={disabled} hidden />
          <Col span={6}>
            <InputNumberField
              disabled={disabled}
              name={[...fieldPrefix, "pid"]}
              label={
                <>
                  <FormattedMessage id="AudioCodec.pid" defaultMessage="PID" />
                  <StyledTooltip placement="top" title={formatMessage(translations.automaticAs0Tooltip)}>
                    <QuestionCircleFilled />
                  </StyledTooltip>
                </>
              }
              rules={[pidValidation]}
              min={0}
              max={8186}
            />
          </Col>
          {isMpeg2audioType && <Mpeg2AudioCodec disabled={disabled} fieldPrefix={[...fieldPrefix, "audioCodec"]} />}
          {isAACType && <AACCodec disabled={disabled} fieldPrefix={[...fieldPrefix, "audioCodec"]} />}
          <Col span={6}>
            <Item
              name={[...fieldPrefix, "convert"]}
              valuePropName="checked"
              label={<FormattedMessage id="GeneralVideoCodec.convert" defaultMessage="Convert" />}
            >
              <Switch
                disabled={disabled}
                onChange={handleSwitchConvert}
                defaultChecked={isSwitchConvert}
                checkedChildren={formatMessage(globalTranslations.on)}
                unCheckedChildren={formatMessage(globalTranslations.off)}
              />
            </Item>
          </Col>
          {isSwitchConvert && (
            <GlobalAudioCodecFormItem
              disabled={disabled}
              prefix={[...fieldPrefix, "audioFormat"]}
              isAudioFormat
              audioCodecType={audioCodecType}
            />
          )}
        </Row>
      </AdvancedSettings>
    </>
  );
}

const StyledTooltip = styled(Tooltip)`
  margin-left: 5px;
`;

AudioCodec.propTypes = {
  disabled: PropTypes.bool,
  prefix: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  fieldPrefix: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
};

AudioCodec.defaultProps = {
  disabled: false,
};
